// Page title lookup table for Google Analytics tracking
// This maps route paths to their corresponding page titles

export interface PageTitleEntry {
  title: string;
  description?: string;
}

export const PAGE_TITLES: Record<string, PageTitleEntry> = {
  // Main/Home pages
  "/": {
    title: "Home",
    description: "Main landing page",
  },

  // Authentication pages
  "/login": {
    title: "Login",
    description: "User login page",
  },
  "/join": {
    title: "Join",
    description: "User registration/signup page",
  },
  "/forgot-password": {
    title: "Forgot Password",
    description: "Password recovery page",
  },
  "/quick-register": {
    title: "Quick Register",
    description: "Quick registration for invitations",
  },

  // User flow pages
  "/congrats": {
    title: "Congratulations",
    description: "Registration completion/success page",
  },
  "/payment-status": {
    title: "Payment Status",
    description: "Payment processing status page",
  },

  // Main app pages
  "/meal-plan": {
    title: "Meal Plan",
    description: "User meal plan dashboard",
  },
  "/dietitian-appointment": {
    title: "Dietitian Appointment",
    description: "Book appointment with dietitian",
  },

  // Partner/Plan pages
  "/partners": {
    title: "Partners",
    description: "Diet center partners listing",
  },
  "/partners/:partner": {
    title: "Partner Details",
    description: "Individual partner/diet center page",
  },
  "/partners/:partner/:plan": {
    title: "Plan Details",
    description: "Specific meal plan details",
  },
  "/partners/:partner/:plan/day-selection": {
    title: "Day Selection",
    description: "Select delivery days",
  },
  "/partners/:partner/:plan/meal-selection": {
    title: "Meal Selection",
    description: "Choose specific meals",
  },
  "/partners/:partner/:plan/delivery-options": {
    title: "Delivery Options",
    description: "Select delivery preferences",
  },
  "/partners/:partner/:plan/address-selection": {
    title: "Address Selection",
    description: "Choose delivery address",
  },
  "/partners/:partner/:plan/summary": {
    title: "Order Summary",
    description: "Review order before completion",
  },

  // Profile pages
  "/profile": {
    title: "Profile",
    description: "User profile main page",
  },
  "/profile/info": {
    title: "Profile",
    description: "User profile main page",
  },
  "/profile/plan-info": {
    title: "Plan Information",
    description: "Current subscription plan details",
  },
  "/profile/edit": {
    title: "Edit Profile",
    description: "Edit user profile information",
  },
  "/profile/health-analytics": {
    title: "Health Analytics",
    description: "Health metrics and analytics",
  },
  "/profile/address": {
    title: "Address Management",
    description: "Manage delivery addresses",
  },
  "/profile/change-password": {
    title: "Change Password",
    description: "Update account password",
  },
  "/profile/wallet": {
    title: "Wallet",
    description: "Wallet balance and transaction history",
  },

  // Business pages
  "/partner-application": {
    title: "Partner Application",
    description: "Application to become a partner",
  },
  "/about": {
    title: "About Us",
    description: "Company information and mission",
  },

  // Legal/Support pages
  "/privacy": {
    title: "Privacy Policy",
    description: "Privacy policy and data handling",
  },
  "/terms-and-conditions": {
    title: "Terms and Conditions",
    description: "Terms of service and conditions",
  },
  "/support": {
    title: "Support",
    description: "Customer support and help",
  },
  "/contact": {
    title: "Contact Us",
    description: "Contact information and form",
  },

  // Preference pages
  "/recommended-plans": {
    title: "Recommended Plans",
    description: "Set meal plan preferences",
  },
  "/diet-preference": {
    title: "Diet Preference",
    description: "Set dietary preferences",
  },
  "/all-plans": {
    title: "All Plans",
    description: "View all plans",
  },
  "/find-best-plan": {
    title: "Find Best Plan",
    description: "Find the best meal plan",
  },

  // Invitation pages
  "/inv/:code": {
    title: "Invitation",
    description: "Invitation acceptance page",
  },

  // App redirect
  "/app": {
    title: "Mobile App",
    description: "Mobile app download/redirect",
  },

  // Error pages
  "/404": {
    title: "Page Not Found",
    description: "404 error page",
  },
  "*": {
    title: "Page Not Found",
    description: "Default 404 page",
  },
};

/**
 * Get page title for a given path
 * Handles dynamic routes by matching patterns
 */
export const getPageTitle = (path: string): string => {
  // Remove query parameters for pattern matching
  const pathWithoutQuery = path.split("?")[0];

  // Direct match first
  if (PAGE_TITLES[pathWithoutQuery]) {
    return PAGE_TITLES[pathWithoutQuery].title;
  }

  // Handle dynamic routes by checking patterns
  const patterns = [
    { pattern: /^\/inv\/[^\/]+/, title: "Invitation" },
    { pattern: /^\/partners\/[^\/]+\/[^\/]+\/day-selection/, title: "Day Selection" },
    { pattern: /^\/partners\/[^\/]+\/[^\/]+\/meal-selection/, title: "Meal Selection" },
    { pattern: /^\/partners\/[^\/]+\/[^\/]+\/delivery-options/, title: "Delivery Options" },
    { pattern: /^\/partners\/[^\/]+\/[^\/]+\/address-selection/, title: "Address Selection" },
    { pattern: /^\/partners\/[^\/]+\/[^\/]+\/summary/, title: "Order Summary" },
    { pattern: /^\/partners\/[^\/]+\/[^\/]+/, title: "Plan Details" },
    { pattern: /^\/partners\/[^\/]+/, title: "Partner Details" },
  ];

  for (const { pattern, title } of patterns) {
    if (pattern.test(pathWithoutQuery)) {
      return title;
    }
  }

  // Default fallback
  return "Page Title Not Found";
};

/**
 * Get page description for a given path
 */
export const getPageDescription = (path: string): string | undefined => {
  // Remove query parameters for pattern matching
  const pathWithoutQuery = path.split("?")[0];

  if (PAGE_TITLES[pathWithoutQuery]) {
    return PAGE_TITLES[pathWithoutQuery].description;
  }

  // Handle dynamic routes
  const patterns = [
    { pattern: /^\/inv\/[^\/]+/, description: "Invitation acceptance page" },
    { pattern: /^\/partners\/[^\/]+\/[^\/]+\/day-selection/, description: "Select delivery days" },
    { pattern: /^\/partners\/[^\/]+\/[^\/]+\/meal-selection/, description: "Choose specific meals" },
    { pattern: /^\/partners\/[^\/]+\/[^\/]+\/delivery-options/, description: "Select delivery preferences" },
    { pattern: /^\/partners\/[^\/]+\/[^\/]+\/address-selection/, description: "Choose delivery address" },
    { pattern: /^\/partners\/[^\/]+\/[^\/]+\/summary/, description: "Review order before completion" },
    { pattern: /^\/partners\/[^\/]+\/[^\/]+/, description: "Specific meal plan details" },
    { pattern: /^\/partners\/[^\/]+/, description: "Individual partner/diet center page" },
  ];

  for (const { pattern, description } of patterns) {
    if (pattern.test(pathWithoutQuery)) {
      return description;
    }
  }

  return undefined;
};
